import { AuditControllerApi, AuditRecord, AuditReportRequest } from '../../../api/api';
import { BaseApi, configuration } from '../../../api/BaseApi';
import { processError } from '../../../api/customFetch';
import { ChangeLogEntry } from './changeLogHistoryModal';

// ─── Entity types ─────────────────────────────────────────────────────────────

export type ChangeLogEntityType = 'PUBLISHER' | 'DSP' | 'DEAL';

// ─── Flatten one AuditRecord → N ChangeLogEntry rows (one per changed field) ─
//
// API response per record:
// {
//   changedBy:   "admin",
//   changedTime: "2026-02-20 09:40:08",
//   action:      "Update",
//   changeLogMap: {
//     "name":   { previousValue: "Equativ DSP",  currentValue: "Equativ DSP11" },
//     "status": { previousValue: "Active",        currentValue: "Paused"        }
//   }
// }

function flattenRecord(r: AuditRecord): ChangeLogEntry[] {
    const dt   = r.changedTime ?? '';
    const user = r.changedBy   ?? '';

    if (!r.changeLogMap || Object.keys(r.changeLogMap).length === 0) {
        return [{ dt, user, field: r.action ?? '', ov: undefined, nv: undefined }];
    }

    return Object.entries(r.changeLogMap).map(([fieldName, change]) => ({
        dt,
        user,
        field: fieldName,
        ov:    change.previousValue ?? undefined,
        nv:    change.currentValue  ?? undefined,
    }));
}

// ─── API class ────────────────────────────────────────────────────────────────

class ChangeLogApi extends BaseApi {
    api: AuditControllerApi;

    constructor() {
        super();
        this.api = new AuditControllerApi(configuration, configuration.basePath, this.customFetch);
    }

    /**
     * POST /api/audit/report
     *
     * @param entityType  "PUBLISHER" | "DSP" | "DEAL"
     * @param startDate   "YYYY-MM-DD"  — time 00:00 is appended automatically
     * @param endDate     "YYYY-MM-DD"  — time 23:59 is appended automatically
     * @param entityId    ID of the publisher / DSP / deal being edited
     */
    async fetchChangeLogs(
        entityType: ChangeLogEntityType,
        startDate: string,
        endDate: string,
        entityId: number | string
    ): Promise<ChangeLogEntry[]> {
        try {
            const body: AuditReportRequest = {
                entityType,
                entityId,
                startDate: `${startDate} 00:00`,
                endDate:   `${endDate} 23:59`,
            };

            const response = await this.api.auditReport(body);

            // Resolve the actual array regardless of response wrapper shape:
            // • bare array:         [{ changedBy, ... }, ...]
            // • { data: [...] }:    response.data
            // • { items: [...] }:   response.items
            let records: AuditRecord[];

            if (Array.isArray(response)) {
                records = response;
            } else if (response && Array.isArray((response as any).data)) {
                records = (response as any).data;
            } else if (response && Array.isArray((response as any).items)) {
                records = (response as any).items;
            } else {
                // Last resort: find first array value in the response object
                const firstArray = Object.values(response as object).find(Array.isArray);
                records = Array.isArray(firstArray) ? firstArray : [];
            }

            // Flatten: each record may contain multiple field changes
            return records.flatMap(flattenRecord);

        } catch (err) {
            return processError(err);
        }
    }
}

const changeLogApi = new ChangeLogApi();

export default changeLogApi;